import React from "react";
import { StatusCell as GeneralStatusCell, StatusOption } from "./StatusCell";
import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/components/pages/reservations/utils/formatDateTime";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PreCheckCellProps {
  status?: PreCheckStatus;
  className?: string;
  readAt?: Date | string | null;
  enablePreview?: boolean;
  notDoneLabelKey?: string;
}

export const PreCheckCell: React.FC<PreCheckCellProps> = ({
  status,
  className,
  readAt,
  enablePreview = false,
  notDoneLabelKey = "reservation.precheckin.states.notCompleted",
}) => {
  const { t } = useTranslation();
  const preCheckStatuses: StatusOption[] = [
    {
      status: PreCheckStatus.NOT_DONE,
      text: t(notDoneLabelKey),
      color: "bg-light-orange/5 border-light-orange/50 text-light-orange",
    },
    {
      status: PreCheckStatus.DONE,
      text: t("reservation.precheckin.states.completed"),
      color: "bg-light-green/5 border-light-green/50 text-light-green",
    },
    {
      status: PreCheckStatus.SENT,
      text: t("reservation.precheckin.states.sent1Day"),
      color: "bg-dark-indigo/5 border-dark-indigo/50 text-dark-indigo",
    },
    {
      status: PreCheckStatus.SENT_3,
      text: t("reservation.precheckin.states.sent3Day"),
      color: "bg-dark-indigo/5 border-dark-indigo/50 text-dark-indigo",
    },
    {
      status: PreCheckStatus.SENT_7,
      text: t("reservation.precheckin.states.sent7Day"),
      color: "bg-dark-indigo/5 border-dark-indigo/50 text-dark-indigo",
    },
    {
      status: PreCheckStatus.NOT_SEND,
      text: t("reservation.precheckin.states.notSent"),
      color: "bg-dark-orange/5 border-dark-orange/50 text-dark-orange",
    },
  ];

  const cell = (
    <GeneralStatusCell
      statuses={preCheckStatuses}
      currentStatus={PreCheckStatus[status!]}
      className={className}
    />
  );

  const isSentStatus =
    status === PreCheckStatus.SENT ||
    status === PreCheckStatus.SENT_3 ||
    status === PreCheckStatus.SENT_7;

  const previewState = (() => {
    if (!enablePreview || !status) {
      return null;
    }

    if (status === PreCheckStatus.NOT_DONE) {
      return {
        title: t("reservation.precheckin.preview.clicked"),
      };
    }

    if (isSentStatus) {
      if (readAt) {
        return {
          title: t("reservation.precheckin.preview.seen"),
          subtitle: formatDateTime(readAt, "dd.MM.yyyy - hh:mm a"),
        };
      }

      return {
        title: t("reservation.precheckin.preview.notSeen"),
      };
    }

    return null;
  })();

  if (!previewState) {
    return cell;
  }

  const previewContent = (
    <div className="space-y-1">
      <div className="text-sm font-medium text-foreground">
        {previewState.title}
      </div>
      {previewState.subtitle ? (
        <div className="text-xs text-muted-foreground">
          {previewState.subtitle}
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="block w-full rounded-lg bg-transparent p-0 text-left cursor-pointer"
              onClick={(event) => event.stopPropagation()}
            >
              {cell}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="w-auto max-w-60 px-3 py-2">
          {previewContent}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto max-w-60 px-3 py-2"
        onClick={(event) => event.stopPropagation()}
      >
        {previewContent}
      </PopoverContent>
    </Popover>
  );
};
